import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import useApi from "../../hooks/useApi.js";
import {useToast} from "../../contexts/toast.js";
import useMountedState from "../../hooks/useMountedState.js";
import {newFromBuilder} from "../../utils/builder.js";
import {flattenInstanceRows, mergeTemplateIntoComponent} from "../../utils/templatePropagation.js";

import Modal from "../../components/Modal.js";
import ModalBuilderVersionNote from "../general/ModalBuilderVersionNote.js";

import {pad} from "../../style/components/variables.js";
import {
  Button,
  ButtonLoader,
  Error,
  HeadingMedium,
  Inline,
  Loader,
  NotLoaded,
  Text
} from "../../style/components/general.js";

const ModalPropagateTemplate = ({
  visible,
  setVisible,
  template,
  previousTemplate,
  newTemplate,
  onComplete
}) => {
  const isMounted = useMountedState();
  const {addToast} = useToast();
  const {api} = useApi("checksheets", {
    suppress: {success: true, error: false, longRequest: false}
  });

  const [loading, setLoading] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [showVersionNote, setShowVersionNote] = useState(false);
  const [checksheetsLookup, setchecksheetsLookup] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [results, setResults] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const propagationTemplates = useMemo(
    () => ({previousTemplate, newTemplate}),
    [previousTemplate, newTemplate]
  );

  const rows = useMemo(
    () => flattenInstanceRows(template, checksheetsLookup, propagationTemplates),
    [template, checksheetsLookup, propagationTemplates]
  );

  useEffect(() => {
    if (!isMounted() || !visible || !template?.instances) return;

    setLoading(true);
    setResults(null);
    setRowErrors({});
    setShowVersionNote(false);

    const ids = Object.keys(template.instances);
    api.callGet(null, {ids}).then(response => {
      if (!isMounted()) return;
      if (response?.status === 200 && Array.isArray(response.data)) {
        const byId = Object.fromEntries(response.data.map(c => [`${c.id}`, c]));
        setchecksheetsLookup(byId);
        const initial = new Set(
          flattenInstanceRows(template, byId, propagationTemplates).map(r => r.id)
        );
        setSelectedIds(initial);
      }
      setLoading(false);
    });
  }, [api, isMounted, propagationTemplates, template, visible]);

  const toggleRow = useCallback(id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSkip = () => {
    setShowVersionNote(false);
    setVisible(false);
    onComplete?.();
  };

  const handleRequestPropagate = () => {
    if (rows.filter(r => selectedIds.has(r.id)).length === 0) return;
    setShowVersionNote(true);
  };

  const runPropagate = useCallback(
    async note => {
      const targets = rows.filter(r => selectedIds.has(r.id));
      if (targets.length === 0) return;

      setShowVersionNote(false);
      setPropagating(true);
      setResults(null);
      setRowErrors({});

      const summary = {succeeded: 0, failed: 0, skipped: 0};
      const errors = {};

      const byChecksheet = targets.reduce((acc, row) => {
        if (!acc[row.checksheetId]) acc[row.checksheetId] = [];
        acc[row.checksheetId].push(row);
        return acc;
      }, {});

      const propagateChecksheet = async (checksheetId, sheetRows) => {
        const checksheet = checksheetsLookup[checksheetId];
        if (!checksheet?.builder) {
          sheetRows.forEach(row => {
            summary.failed += 1;
            errors[row.id] = "Checksheet builder not found.";
          });
          return;
        }

        let builder = {
          ...checksheet.builder,
          byId: {...checksheet.builder.byId},
          allIds: [...(checksheet.builder.allIds || [])]
        };

        const rowsToSave = sheetRows.reduce((acc, row) => {
          const facilityById = checksheet.facility?.builder?.byId || null;
          const {
            builder: merged,
            warnings,
            changes
          } = mergeTemplateIntoComponent(builder, row.componentName, {
            previousTemplate,
            newTemplate,
            facilityById
          });

          if (warnings.some(w => /Component not found in builder/i.test(w))) {
            summary.failed += 1;
            errors[row.id] = warnings.join(" ");
            return acc;
          }

          builder = merged;
          const hasChanges =
            changes.added.length + changes.updated.length + changes.removed.length > 0;

          if (warnings.length) errors[row.id] = warnings.join(" ");
          if (!hasChanges && warnings.length) return acc;

          return [...acc, row];
        }, []);

        if (rowsToSave.length === 0) return;

        const componentNames = [...new Set(rowsToSave.map(row => row.componentName))];
        // Persisted builders from GET omit `toggle`; default newFromBuilder would drop every node.
        const checksheetBuilder = newFromBuilder(
          builder.allIds || [],
          builder,
          {allIds: [], byId: {}},
          true,
          true
        );

        if (
          !checksheetBuilder.allIds?.length ||
          !Object.keys(checksheetBuilder.byId || {}).length
        ) {
          sheetRows.forEach(row => {
            summary.failed += 1;
            errors[row.id] =
              errors[row.id] || "Refusing to save: checksheet builder would be empty.";
          });
          return;
        }

        const payload = {builder: checksheetBuilder};

        if (template?.id && componentNames.length > 0) {
          payload.templates = {[template.id]: componentNames};
        }

        payload.note = note;

        const response = await api.callPut(checksheetId, payload);

        if (response?.status === 200) {
          summary.succeeded += rowsToSave.length;
        } else {
          summary.failed += rowsToSave.length;
          const message = typeof response === "string" ? response : "Update failed.";
          rowsToSave.forEach(row => {
            if (!errors[row.id]) errors[row.id] = message;
          });
        }
      };

      await Object.entries(byChecksheet).reduce(
        (chain, [checksheetId, sheetRows]) =>
          chain.then(() => propagateChecksheet(checksheetId, sheetRows)),
        Promise.resolve()
      );

      setRowErrors(errors);
      setResults(summary);
      setPropagating(false);
      onComplete?.(summary);
      if (summary.succeeded > 0) addToast("Checksheet updates successful!", "success");
    },
    [
      addToast,
      api,
      checksheetsLookup,
      newTemplate,
      onComplete,
      previousTemplate,
      rows,
      selectedIds,
      template?.id
    ]
  );

  const handleNoteSubmit = note => {
    runPropagate(note);
  };

  const renderRow = row => {
    const error = rowErrors[row.id];

    return (
      <Row key={row.id}>
        <RowLabel>
          <input
            type="checkbox"
            checked={selectedIds.has(row.id)}
            disabled={propagating || !!results}
            onChange={() => toggleRow(row.id)}
          />
          <Facility>
            {row.facilityName?.toUpperCase()}&nbsp;{row.facilityType}
          </Facility>
          &nbsp;{">"}&nbsp;
          <Checksheet>
            {row.checksheetName?.toUpperCase()}
            {row.frequencyName ? ` (${row.frequencyName.toUpperCase()})` : ""}
          </Checksheet>
          &nbsp;{">"}&nbsp;
          <Group>{row.groupLabel}</Group>&nbsp;{">"}&nbsp;
          <Component>{row.componentLabel}</Component>
        </RowLabel>
        {error && <RowError>{error}</RowError>}
      </Row>
    );
  };

  return (
    <>
    <Modal
      testId="template.propagate"
      visible={visible && !showVersionNote}
      setVisible={setVisible}
      width="mincontent"
      clickOutsideToClose={!propagating && !showVersionNote}>
      <HeadingMedium>Update linked components?</HeadingMedium>

      <Text>
        The following components will be updated with the changes just saved to this template:
      </Text>
      <br />

      {loading ? (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      ) : (
        <>
          {rows.length > 0 ? (
            <RowList>{rows.map(renderRow).filter(Boolean)}</RowList>
          ) : (
            <Text>
              No linked components on checksheets can receive these template changes (missing
              component, ambiguous duplicate fields, or no matching fields).
            </Text>
          )}
          {results && (
            <Summary>
              Updated {results.succeeded} component{results.succeeded === 1 ? "" : "s"}
            </Summary>
          )}
        </>
      )}

      <Actions>
        {!results ? (
          <>
            {!loading && (
              <Button
                type="button"
                onClick={handleRequestPropagate}
                disabled={propagating || showVersionNote || loading || selectedIds.size === 0}
                loading={propagating ? 1 : 0}>
                Update selected
                {propagating && <ButtonLoader />}
              </Button>
            )}
            <Button type="button" onClick={handleSkip} disabled={propagating || showVersionNote}>
              Skip
            </Button>
          </>
        ) : (
          <Button type="button" onClick={handleSkip}>
            Done
          </Button>
        )}
      </Actions>
    </Modal>

    {showVersionNote && (
      <ModalBuilderVersionNote
        visible={showVersionNote}
        setVisible={state => {
          if (!state) setShowVersionNote(false);
        }}
        submitNote={handleNoteSubmit}
        cancel={() => setShowVersionNote(false)}
        goBack={() => setShowVersionNote(false)}
      />
    )}
    </>
  );
};

ModalPropagateTemplate.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  template: PropTypes.objectOf(PropTypes.any),
  previousTemplate: PropTypes.objectOf(PropTypes.any),
  newTemplate: PropTypes.objectOf(PropTypes.any),
  onComplete: PropTypes.func
};

const RowList = styled.div`
  max-height: 20rem;
  overflow-y: auto;
  margin-bottom: ${pad * 2}px;
`;

const Row = styled.div`
  margin-bottom: ${pad}px;
`;

const RowLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${pad}px;
  cursor: pointer;
`;

const RowError = styled(Error)`
  margin-left: ${pad * 3}px;
  margin-top: ${pad / 2}px;
`;

const Facility = styled.span`
  white-space: nowrap;
`;

const Checksheet = styled.span`
  white-space: nowrap;
`;

const Group = styled.span`
  color: ${props => props.theme.group};
  white-space: nowrap;
`;

const Component = styled.span`
  color: ${props => props.theme.component};
  white-space: nowrap;
`;

const Summary = styled(Text)`
  margin-bottom: ${pad}px;
`;

const Actions = styled(Inline)`
  gap: ${pad}px;
  margin-top: ${pad}px;
`;

export default ModalPropagateTemplate;
